import { memo, useCallback } from 'react';
import { useDraggable } from '@dnd-kit/core';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { zoomPositionChanged, selectZoomState } from 'features/content-zoom/contentZoomSlice';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useBoundaryConstraints } from 'features/content-zoom/hooks/useBoundaryConstraints';
import cx from 'clsx';

const getCursor = (scale: number, isDragging: boolean): string => {
  if (scale <= 1) {
    return 'default';
  }
  return isDragging ? 'grabbing' : 'grab';
};

const ZoomContainer = styled('div')({
  width: '100%',
  height: '100%',
  position: 'absolute',
  top: 0,
  left: 0,
  zIndex: 1,
  overflow: 'hidden',
});

const ZoomableContentStyled = styled('div')<{
  isDragging: boolean;
  scale: number;
}>(({ isDragging, scale }) => ({
  width: '100%',
  height: '100%',
  transformOrigin: 'center center',
  transition: isDragging ? 'none' : 'transform 0.2s ease-out',
  touchAction: scale > 1 ? 'none' : 'auto',
  userSelect: 'none',
  overscrollBehavior: 'none',
  WebkitTapHighlightColor: 'transparent',
}));

const DragOverlay = styled('div')<{ isDragging: boolean }>(({ isDragging }) => ({
  position: 'absolute',
  inset: 0,
  zIndex: 1000,
  touchAction: 'none',
  cursor: isDragging ? 'grabbing' : 'grab',
  '&.disabled': {
    cursor: 'default',
  },
}));

interface ZoomableContentProps {
  zoomId: string;
  children: React.ReactNode;
  config?: {
    maxDragDistance?: number;
    dragResistance?: number;
  };
  ariaLabel?: string;
}

export const ZoomableContent = memo(
  ({ zoomId, children, config = {}, ariaLabel }: ZoomableContentProps) => {
    const { t } = useTranslation('room');
    const dispatch = useAppDispatch();
    const zoomState = useAppSelector((state) => selectZoomState(state, zoomId));
    const { scale = 1, offsetX = 0, offsetY = 0 } = zoomState || {};

    const handlePositionChange = useCallback(
      (newOffsetX: number, newOffsetY: number) => {
        if (newOffsetX !== offsetX || newOffsetY !== offsetY) {
          dispatch(zoomPositionChanged({ zoomId, offsetX: newOffsetX, offsetY: newOffsetY }));
        }
      },
      [dispatch, zoomId, offsetX, offsetY]
    );

    const { containerRef, constrainPosition, applyResistance, isPositionOutOfBounds } =
      useBoundaryConstraints({
        scale,
        offsetX,
        offsetY,
        config: {
          maxDragDistance: 100,
          dragResistance: 0.6,
          ...config,
        },
        onPositionChange: handlePositionChange,
      });

    const disabled = scale <= 1;

    const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
      id: `zoom-${zoomId}`,
      disabled,
      data: {
        zoomId,
        currentOffsetX: offsetX,
        currentOffsetY: offsetY,
        scale,
        constrainPosition,
        applyResistance,
        isPositionOutOfBounds,
        maxDragDistance: config.maxDragDistance || 100,
      },
      attributes: {
        role: 'img',
        roleDescription: 'draggable content',
      },
    });

    const calculateDragPosition = () => {
      let currentOffsetX = offsetX;
      let currentOffsetY = offsetY;

      if (isDragging && transform) {
        const normalizedDeltaX = transform.x / scale;
        const normalizedDeltaY = transform.y / scale;

        const newX = offsetX + normalizedDeltaX;
        const newY = offsetY + normalizedDeltaY;

        const resistedPosition = applyResistance(newX, newY);
        currentOffsetX = resistedPosition.offsetX;
        currentOffsetY = resistedPosition.offsetY;
      }

      return { currentOffsetX, currentOffsetY };
    };

    const { currentOffsetX, currentOffsetY } = calculateDragPosition();

    const contentStyle = {
      transform: `matrix(${scale}, 0, 0, ${scale}, ${currentOffsetX * scale}, ${currentOffsetY * scale})`,
      cursor: getCursor(scale, isDragging),
    };

    const isZoomed = scale > 1;

    return (
      <ZoomContainer ref={containerRef}>
        <ZoomableContentStyled
          ref={setNodeRef}
          isDragging={isDragging}
          scale={scale}
          style={contentStyle}
          {...(!isZoomed ? listeners : {})}
          {...attributes}
          aria-label={ariaLabel || t('screenshare.draggable_screen_share_aria_label')}
        >
          {children}
        </ZoomableContentStyled>
        {isZoomed && (
          <DragOverlay className={cx({ disabled })} isDragging={isDragging} {...listeners} />
        )}
      </ZoomContainer>
    );
  }
);
